import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, Trash2, Check, Play, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const generateVehicleSlug = (v) => {
  if (!v) return '';
  const brand = (v.brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const model = (v.model || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(new RegExp(`^${brand}`, 'i'), '');
  const year = v.year || '';
  return `${brand}-${model}-${year}`;
};

const SuspiciousPhotos = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [filter, setFilter] = useState('all'); // all, high, medium

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const sev = filter === 'all' ? '' : `&severity=${filter}`;
      const res = await axios.get(`${API}/admin/suspicious-photos?unreviewed_only=true${sev}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/scan-suspicious-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    fetchStatus();
    const i = setInterval(fetchStatus, 5000);
    return () => clearInterval(i);
  }, [fetchStatus]);

  const startScan = async () => {
    setStarting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/scan-suspicious-photos`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success(t('admin.scanStarted') || 'AI photo scan started');
        fetchStatus();
      } else {
        toast.info(res.data.message || 'Already running');
      }
    } catch (e) {
      toast.error('Scan failed to start');
    } finally {
      setStarting(false);
    }
  };

  const removePhoto = async (vehicleId, imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/vehicles/${vehicleId}/remove-photo`,
        { image_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('admin.photoRemoved') || 'Photo removed');
      fetchItems();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const keepPhoto = async (vehicleId, imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/suspicious-photos/keep`,
        { vehicle_id: vehicleId, image_url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('admin.photoKept') || 'Photo kept');
      fetchItems();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const isRunning = status?.status === 'running';

  return (
    <div className="space-y-6" data-testid="suspicious-photos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="text-orange-500" />
            {t('admin.suspiciousPhotosTitle') || 'Suspicious Photos (AI Scan)'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('admin.suspiciousPhotosSub') ||
              'AI scans every vehicle photo for banners, ads, and wrong-vehicle images.'}
          </p>
        </div>
        <Button
          onClick={startScan}
          disabled={isRunning || starting}
          className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
          data-testid="start-ai-scan-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          {isRunning
            ? `${status.processed_images || 0} / ${status.total_images || '?'}`
            : t('admin.startAIScan') || 'Start AI Scan'}
        </Button>
      </div>

      {isRunning && (
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-md" data-testid="scan-progress">
          <div className="flex items-center gap-3">
            <div className="animate-spin w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-900">
                {t('admin.aiScanInProgress') || 'AI scan in progress'}
              </p>
              <p className="text-xs text-orange-700 mt-1">
                {status.processed_images || 0} / {status.total_images || 0} photos ·
                {' '}
                {status.suspicious_found || 0} {t('admin.flagged') || 'flagged'}
              </p>
            </div>
          </div>
          <div className="w-full bg-orange-200 rounded-full h-2 mt-3">
            <div
              className="bg-orange-600 h-2 rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.round(((status.processed_images || 0) / Math.max(1, status.total_images || 1)) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}

      {status?.status === 'finished' && (
        <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-md text-sm text-green-800">
          {t('admin.scanFinished') || 'Scan finished'} — {status.suspicious_found} {t('admin.photosFlagged') || 'photos flagged'} ·
          {' '}{status.processed_images} {t('admin.photosScanned') || 'photos scanned'}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2" data-testid="severity-filters">
        {[
          { k: 'all', lbl: t('admin.all') || 'All' },
          { k: 'high', lbl: t('admin.highSeverity') || 'High' },
          { k: 'medium', lbl: t('admin.mediumSeverity') || 'Medium' },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-4 py-2 rounded-md text-sm font-medium border ${
              filter === f.k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid={`filter-${f.k}`}
          >
            {f.lbl}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="py-20 text-center text-gray-500">{t('admin.loading') || 'Loading...'}</div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-gray-500" data-testid="no-suspicious-photos">
          {t('admin.noSuspiciousPhotos') || 'No suspicious photos found. Run a scan to check.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="suspicious-list">
          {items.map((it, idx) => (
            <div
              key={`${it.vehicle_id}-${idx}`}
              className="bg-white border rounded-lg overflow-hidden shadow-sm"
              data-testid={`suspicious-card-${idx}`}
            >
              <img src={it.image_url} alt="suspicious" className="w-full h-52 object-cover bg-gray-100" />
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm truncate">
                    {it.year} {it.brand} {it.model}
                  </div>
                  <Badge
                    className={
                      it.severity === 'high'
                        ? 'bg-red-500 text-white'
                        : it.severity === 'medium'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-400 text-white'
                    }
                  >
                    {it.severity}
                  </Badge>
                </div>
                <div className="flex gap-1 flex-wrap text-xs">
                  {it.is_banner_or_ad && <Badge variant="outline" className="border-red-300 text-red-700">banner/ad</Badge>}
                  {!it.is_vehicle_photo && <Badge variant="outline" className="border-orange-300 text-orange-700">not a vehicle</Badge>}
                  {it.likely_wrong_vehicle && <Badge variant="outline" className="border-purple-300 text-purple-700">wrong vehicle</Badge>}
                </div>
                <p className="text-xs text-gray-600 italic">{it.reason}</p>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => removePhoto(it.vehicle_id, it.image_url)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs h-8"
                    data-testid={`remove-photo-${idx}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> {t('admin.remove') || 'Remove'}
                  </Button>
                  <Button
                    onClick={() => keepPhoto(it.vehicle_id, it.image_url)}
                    variant="outline"
                    className="flex-1 border-gray-300 text-xs h-8"
                    data-testid={`keep-photo-${idx}`}
                  >
                    <Check className="w-3 h-3 mr-1" /> {t('admin.keep') || 'Keep'}
                  </Button>
                </div>
                <a
                  href={`/${generateVehicleSlug(it)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline block truncate"
                  title={`${it.year} ${it.brand} ${it.model}`}
                  data-testid={`view-vehicle-link-${idx}`}
                >
                  {t('admin.viewVehicle') || 'View vehicle'} →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuspiciousPhotos;
